import React from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import WorldMap from "../components/learn/maps/WorldMap";
import useLearnProgress from "../hooks/useLearnProgress";
import { useLearnCloudNavigate } from "../components/learn/PixleyCloudTransition";
import { countingLessonByNumber } from "../data/countingLadderCatalog";
import {
  COUNTING_MAP_ASPECT, canOpenCountingLesson, countingActivityRoute, countingMapNodeState, countingWorldById,
  firstUnfinishedActivity, isCountingWorldUnlocked,
} from "../utils/learnCountingProgress";

/** One map for all three counting worlds. */
export default function CountingWorldMapPage() {
  const { worldId } = useParams();
  const navigate = useNavigate();
  const { navigateWithCloud } = useLearnCloudNavigate();
  const { countingSet, loading } = useLearnProgress();
  const world = countingWorldById(worldId);

  if (!world) return <Navigate to="/learn/counting" replace />;
  if (!loading && !isCountingWorldUnlocked(world, countingSet)) return <Navigate to="/learn/counting" replace />;

  const open = (lessonNumber) => {
    if (!canOpenCountingLesson(lessonNumber, countingSet)) return;
    const lesson = countingLessonByNumber(lessonNumber);
    const activity = lesson && firstUnfinishedActivity(lesson, countingSet);
    if (activity) navigateWithCloud(countingActivityRoute(lesson.number, activity.id));
  };

  const nodes = world.nodes.map((c, i) => {
    const number = world.lessonNumbers[i];
    const lesson = countingLessonByNumber(number);
    return {
      id: String(number),
      label: String(i + 1),
      caption: lesson?.title || `Lesson ${number}`,
      sub: lesson ? `${lesson.activities.length} activities` : undefined,
      state: countingMapNodeState(world, i, countingSet),
      x: c.x, y: c.y,
      onSelect: () => open(number),
    };
  });

  const done = nodes.filter((n) => n.state === "complete").length;
  const current = nodes.find((n) => n.state === "current");
  const currentLesson = current && countingLessonByNumber(Number(current.id));

  return (
    <WorldMap
      eyebrow="Counting Worlds"
      title={world.title}
      artKey={`counting/worlds/${world.trailArt}`}
      artAspect={COUNTING_MAP_ASPECT}
      accent={world.theme.accent}
      accentDeep={world.theme.accentDeep}
      nodes={nodes}
      progress={{ done, total: nodes.length }}
      next={current
        ? { title: currentLesson?.title || "Next lesson", subtitle: `Lesson ${current.id} · ${currentLesson?.activities.length || 0} activities`, onStart: () => open(Number(current.id)), cta: done === 0 ? "Start" : "Continue" }
        : done === nodes.length
          ? { title: "World complete!", subtitle: "Every lesson here is done.", onStart: () => navigate("/learn/counting"), cta: "Next world" }
          : null}
      onBack={() => navigate("/learn/counting")}
      backLabel="All worlds"
      companionPose={done === nodes.length ? "cheer" : "idle"}
      tip={current ? `Tap ${current.label} to play.` : undefined}
    />
  );
}
